<template>
  <div class="widget w-stock-screener-widget" :class="['font-size-' + fontSize, { bold: isFontBold }]">
    <c-widget-header hideLinkModeReceive stockScreeningManager printView :wid="wid">
      <slot name="buttons" slot="buttons" />
    </c-widget-header>

    <div class="widget-body">
      <div class="panel filter">
        <v-button
          class="panel-bar p-2"
          :class="!panels.filter ? 'collapsed' : ''"
          :aria-expanded="panels.filter ? 'true' : 'false'"
          aria-controls="collapse-filter-panel"
          @click="onUpdatePanel('filter')"
        >
          <font-awesome-icon
            class="mr-2 indicator"
            :icon="['fas', `${panels.filter ? 'minus-circle' : 'plus-circle'}`]"
          />
          <span class="panel-title">{{ i18n('labels.filter') }}</span>
        </v-button>
        <b-collapse :visible="panels.filter" id="collapse-filter-panel">
          <div class="panel-content">
            <c-widget-control-bar>
              <v-form-group :label="i18n('labels.country')" label-cols="auto">
                <v-form-dropdown
                  class="mr-4"
                  :value="selection.settings.country"
                  detached
                  detachedClass="w-stock-screener-widget"
                  boundary="window"
                >
                  <v-dropdown-item
                    v-for="country in countries"
                    :key="country.value"
                    :value="country.value"
                    @click="onUpdateSetting('country', country.value)"
                  >
                    <v-flag class="mr-2" :countryCode="country.value" />
                    {{ country.text }}
                  </v-dropdown-item>
                </v-form-dropdown>
              </v-form-group>
              <v-form-group :label="i18n('labels.exchange')" label-cols="auto">
                <v-form-dropdown
                  class="mr-4"
                  :value="selection.settings.exchange"
                  detached
                  detachedClass="w-stock-screener-widget"
                  boundary="window"
                >
                  <v-dropdown-item
                    v-for="exchange in exchanges"
                    :key="exchange.value"
                    :value="exchange.value"
                    :disabled="!exchange.available"
                    @click="onUpdateSetting('exchange', exchange.value)"
                  >
                    {{ i18n(`exchanges.${exchange.value}`) }}
                  </v-dropdown-item>
                </v-form-dropdown>
              </v-form-group>
              <v-form-group :label="i18n('labels.filter')" label-cols="auto">
                <v-form-dropdown
                  class="mr-4"
                  :value="selection.settings.sector"
                  detached
                  detachedClass="w-stock-screener-widget"
                  boundary="window"
                >
                  <v-dropdown-item
                    v-for="sector in sectors"
                    :key="sector.value"
                    :value="sector.value"
                    :disabled="!sector.available"
                    @click="onUpdateSetting('sector', sector.value)"
                  >
                    {{ sector.value !== 'all' ? i18n(sector.value, 'sectors') : i18n(`sectors.all`) }}
                  </v-dropdown-item>
                </v-form-dropdown>
              </v-form-group>
            </c-widget-control-bar>
          </div>
        </b-collapse>
      </div>
      <div class="panel criteria">
        <v-button
          class="panel-bar p-2"
          :class="!panels.criteria ? 'collapsed' : ''"
          :aria-expanded="panels.criteria ? 'true' : 'false'"
          aria-controls="collapse-criteria-panel"
          @click="onUpdatePanel('criteria')"
        >
          <font-awesome-icon
            class="mr-2 indicator"
            :icon="['fas', `${panels.criteria ? 'minus-circle' : 'plus-circle'}`]"
          />
          <span class="panel-title">{{ i18n('labels.subCriteria') }}</span>
        </v-button>
        <b-collapse :visible="panels.criteria" id="collapse-criteria-panel">
          <div class="panel-content p-2">
            <b-container fluid>
              <b-row>
                <b-col cols="5">
                  <div class="add-criteria-header pt-2 pb-2">{{ i18n('labels.addCriteria') }}</div>
                  <div class="add-criteria-content d-flex">
                    <b-list-group
                      ref="criteria"
                      class="criteria-selection flex-grow-1"
                      role="menu"
                      :aria-label="
                        `${i18n('labels.criteria')} ${i18n('labels.navigationSideArrows')} ${i18n('labels.pressEnter')}`
                      "
                    >
                      <b-list-group-item
                        v-for="filterGroup in filterGroups"
                        :key="filterGroup.id"
                        role="menuitem"
                        class="pt-1 pb-1 border-left-0"
                        :active="selection.criteria.filterGroup === filterGroup.id"
                        :disabled="!filterGroups[filterGroup.id].fids.length"
                        @click="onChangeFilterGroup(filterGroup.id)"
                        tabindex="0"
                        @keydown.enter="onChangeFilterGroup(filterGroup.id)"
                        @keydown.up="navigateUp"
                        @keydown.down="navigateDown"
                        @keydown.right="navigateRight"
                        @keydown.tab="tabFromCriteria"
                      >
                        <span class="sr-only">{{ i18n('labels.criteria') }}</span>
                        {{ i18n(`criteria.${filterGroup.id}`) }}
                        <font-awesome-icon
                          v-if="isFilterGroupActive(filterGroup.id)"
                          class="ml-1"
                          :icon="['fas', 'check']"
                        />
                        <span class="sr-only">{{
                          isFilterGroupActive(filterGroup.id)
                            ? $t(`white-label.globalLabels.checked`)
                            : $t(`white-label.globalLabels.unchecked`)
                        }}</span>
                      </b-list-group-item>
                    </b-list-group>
                    <b-list-group
                      ref="subCriteria"
                      class="sub-criteria-selection flex-grow-1"
                      role="menu"
                      :aria-label="
                        `${selection.criteria.filterGroup} ${i18n('labels.subCriteria')} ${i18n(
                          'labels.navigationSideArrows'
                        )} ${i18n('labels.pressEnter')}`
                      "
                    >
                      <b-list-group-item
                        v-for="(filter, index) in filters"
                        :key="filter.id"
                        role="menuitem"
                        class="pt-1 pb-1 border-top-0 border-bottom-0 border-left-0 border-right-0"
                        :active="isFilterActive(filter.id)"
                        :disabled="isFilterDisabled(filter.id)"
                        @click="onAddFilter(filter)"
                        tabindex="0"
                        @keydown.enter="onAddFilter(filter)"
                        @keydown.up="navigateUp"
                        @keydown.down="navigateDown"
                        @keydown.left="navigateLeft"
                        @keydown.tab="tabFromSubCriteria"
                      >
                        <span class="sr-only">{{
                          selection.criteria.filterGroup + ' ' + i18n('labels.subCriteria')
                        }}</span>
                        {{ i18n(`subcriteria.${filter.id}`) }}
                        <font-awesome-icon v-if="isFilterActive(filter.id)" class="ml-1" :icon="['fas', 'check']" />
                        <span class="sr-only">{{
                          isFilterActive(filter.id)
                            ? $t(`white-label.globalLabels.checked`)
                            : $t(`white-label.globalLabels.unchecked`)
                        }}</span>
                        <span v-if="index === filters.length - 1" class="sr-only">
                          {{ i18n('labels.endOfCriteriaList') }}
                        </span>
                      </b-list-group-item>
                    </b-list-group>
                  </div>
                </b-col>
                <b-col>
                  <div class="edit-criteria-header pt-2 pb-2">{{ i18n('labels.editCriteria') }}</div>
                  <div class="edit-criteria-content">
                    <b-list-group ref="editCriteria">
                      <b-list-group-item
                        v-for="filter in histogramFilters"
                        :key="filter.id"
                        class="p-0 border-top-0 border-left-0 border-right-0"
                      >
                        <!--
                          NOTE: check for undefined only needed as long as database is not handling JSON correctly
                                -> Can be replaced with "null" in later refactoring when this issue is resolved
                        -->
                        <c-histogram-filter
                          v-if="
                            filter.type !== 'pattern' &&
                              filter.extremes.min !== undefined &&
                              filter.extremes.max !== undefined
                          "
                          :filter="filter"
                          :resolution="histogramResolution"
                          @updateHistogramFilter="onUpdateHistogramFilter"
                          @removeFilter="onRemoveFilter"
                        />
                        <c-pattern-filter
                          v-else
                          :filter="filter"
                          @updatePatternFilter="onUpdatePatternFilter"
                          @removeFilter="onRemoveFilter"
                        />
                      </b-list-group-item>
                    </b-list-group>
                  </div>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <div class="controls-bar p-2 d-flex justify-content-end">
                    <v-form-group :label="i18n('labels.savedSearches')" label-cols="auto" class="mb-0">
                      <v-dropdown
                        class="mr-3"
                        :value="selectedScreening ? selectedScreening : 'unsavedSearch'"
                        :text="!screening.name ? i18n('labels.savedSearches') : screening.name"
                        :disabled="screenings.length < 2"
                      >
                        <v-dropdown-item
                          v-for="screening in screenings"
                          :key="screening.id"
                          :value="screening.id"
                          :class="{ 'd-none': screening.id === 'unsavedSearch' }"
                          @click="onScreeningSelected(screening.id)"
                        >
                          {{ screening.name }}
                        </v-dropdown-item>
                      </v-dropdown>
                    </v-form-group>
                    <c-manipulate-screening
                      :buttonDisabled="Boolean(selectedScreening) || (!selectedScreening && !histogramFilters.length)"
                      :successCallback="onScreeningCreated"
                      :filters="screening.filters"
                    />
                    <v-button class="mr-3" variant="secondary" @click="clearAll" :disabled="!histogramFiltersActive">
                      {{ i18n('buttons.clearAll') }}
                    </v-button>
                    <v-button variant="primary" :disabled="resultsCount === 0" @click="showResults">
                      {{ `${i18n('buttons.view')} ${resultsCount} ${i18n('buttons.results')}` }}
                    </v-button>
                  </div>
                </b-col>
              </b-row>
            </b-container>
          </div>
        </b-collapse>
      </div>
      <div class="panel results">
        <v-button
          class="panel-bar p-2"
          :class="!panels.results ? 'collapsed' : ''"
          :disabled="!resultsShown"
          :aria-expanded="panels.results ? 'true' : 'false'"
          aria-controls="collapse-results-panel"
          @click="onUpdatePanel('results')"
        >
          <font-awesome-icon
            class="mr-2 indicator"
            :icon="['fas', `${panels.results ? 'minus-circle' : 'plus-circle'}`]"
          />
          <span class="panel-title">{{ i18n('labels.results') }}</span>
        </v-button>
        <b-collapse :visible="panels.results" id="collapse-results-panel">
          <c-streaming-error :show="hasStreamingError" />
          <div class="panel-content">
            <b-table
              v-if="resultsShown"
              dark
              striped
              hover
              small
              responsive
              no-local-sorting
              no-sort-reset
              :fields="fields"
              :items="items"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              @row-clicked="onRowClicked"
              :aria-label="i18n('labels.results')"
            >
              <template v-for="(field, i) in fields" #[`cell(${field.key})`]="data">
                <div :key="i" :class="cellClass(field.key, data)">
                  <c-function-button-menu
                    v-if="field.key === 'ACTION'"
                    :wid="wid"
                    :data="{ 'q.RIC': data.item.RIC, 'q._COUNTRY': data.item.COUNTRY, 'q._TYPE': data.item.TYPE }"
                    :disabledActions="['delete', 'edit', 'editAlert']"
                  >
                  </c-function-button-menu>
                  <v-flag v-else-if="field.key === 'COUNTRY'" class="mr-2" :countryCode="data.value" />
                  <!-- Bid/Ask -->
                  <c-open-order-entry
                    v-else-if="['q._BID', 'q._ASK'].includes(field.key)"
                    :action="field.key === 'q._BID' ? 'bid' : 'ask'"
                    :limit="data.value"
                    :ric="data.item.RIC"
                    ticketType="stock"
                    :quantity="defaultQuantity"
                  >
                    <c-tick-flash :value="data.value">
                      {{ data.value }}
                    </c-tick-flash>
                  </c-open-order-entry>
                  <!-- Other Streaming columns -->
                  <c-tick-flash v-else-if="streamingFids.includes(field.key)" :value="data.value">
                    {{ data.value }}
                  </c-tick-flash>
                  <div v-else>
                    {{ data.value }}
                  </div>
                </div>
              </template>
            </b-table>
            <div class="d-flex justify-content-center">
              <v-pagination
                v-if="resultsShown"
                v-model="currentPage"
                :total-rows="resultsCount"
                :per-page="perPage"
                @input="fetch"
                aria-controls="my-table"
              />
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import CWidgetHeader from '@custom/widgetContainer/CWidgetHeader';
import CWidgetControlBar from '@custom/widgetContainer/CWidgetControlBar';
import widgetCommons from '@mixins/widgetCommons';
import CHistogramFilter from '@custom/CHistogramFilter';
import CPatternFilter from '@custom/CPatternFilter';
import CManipulateScreening from '@custom/CManipulateScreening';
import stockScreenerBase from '@/utils/stockScreenerBase.json';
import { API } from '@state/api';
import CFunctionButtonMenu from '@custom/CFunctionButtonMenu';
import CTickFlash from '@custom/CTickFlash';
import CStreamingError from '@custom/CStreamingError';
import streamableFidsFilter from '@/utils/streamableFids';
import COpenOrderEntry from '@custom/COpenOrderEntry';
import orderUtils from '@widgets/WOrderEntryWidget/orderUtils.js';

export default {
  name: 'w-stock-screener-widget',
  mixins: [widgetCommons, orderUtils],
  components: {
    CWidgetHeader,
    CWidgetControlBar,
    CHistogramFilter,
    CPatternFilter,
    CManipulateScreening,
    CFunctionButtonMenu,
    CTickFlash,
    CStreamingError,
    COpenOrderEntry
  },
  props: {
    wid: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      items: [],
      histogramFiltersData: {},
      histogramResolution: 500,
      resultsCount: 0,
      results: [],
      resultsShown: false,
      perPage: 20,
      currentPage: 1,
      unavailableFilters: [],
      //settingsChanged: false,
      backgroundFids: ['COUNTRY', 'TYPE'],
      formats: {},
      subscriptionId: null,
      streamingFids: ['q._BID', 'q._ASK', 'q._TRDPRC_1', 'q._NETCHNG_1'],
      hasStreamingError: false,
      allCanadaExchanges: undefined,
      allUSExchanges: undefined
    };
  },
  watch: {
    country: {
      handler() {
        //this.settingsChanged = true;
        this.updateView();
      }
    },
    exchange: {
      handler() {
        //this.settingsChanged = true;
        this.updateView();
      }
    },
    sector: {
      handler() {
        //this.settingsChanged = true;
        this.updateView();
      }
    },
    screenings: {
      immediate: true,
      handler() {
        if (this.selectedScreening && !this.screenings.find(screening => screening.id === this.selectedScreening)) {
          this.onScreeningSelected('');
        }
      }
    },
    sortBy: {
      handler() {
        this.onSortingChanged();
      }
    },
    sortDesc: {
      handler() {
        this.onSortingChanged();
      }
    },
    'screening.filters': {
      handler(val) {
        if (val) {
          const fids = val.map(f =>
            f.fid.includes('.') ? f.fid.replace(',', ';').split('.')[1] : f.fid.replace(',', ';')
          );
          if (this.sortBy && !fids.includes(this.sortBy)) {
            this.$store.commit(`widget/${this.wid}/RESET_SORT`);
          }
        }
      }
    }
  },
  computed: {
    panels() {
      return this.$store.state.widget[this.wid].panels;
    },
    countries() {
      return [
        {
          value: 'CA',
          text: 'Canada'
        },
        {
          value: 'US',
          text: 'USA'
        }
      ];
    },
    exchanges() {
      return stockScreenerBase.filter.exchanges[this.selection.settings.country].map(exchange => exchange);
    },
    sectors() {
      return stockScreenerBase.filter.sectors[this.selection.settings.country].map(sector => sector);
    },
    filterGroups() {
      return stockScreenerBase.criteria; // TODO: To be renamed to filterGroups
    },
    filters() {
      return stockScreenerBase.criteria[this.selection.criteria.filterGroup].fids;
    },
    /**
     * @return what CHistogramFilter receives
     */
    histogramFilters() {
      //console.log('histogramFilters changed');
      if (!Object.keys(this.histogramFiltersData).length && !this.screening.filters.length) {
        return [];
      }
      const result = [];

      this.screening.filters.forEach(filter => {
        const filterID = filter.id; // temporarily as long as the namespaces are not working on API

        // Because on add new filter the new data fetched could be not available yet
        if (filter.type !== 'pattern') {
          if (this.histogramFiltersData[filterID] !== undefined) {
            result.push({
              name: this.i18n('subcriteria.' + filterID),
              data: this.histogramFiltersData[filterID].data,
              extremes: {
                min: isFinite(this.histogramFiltersData[filterID].min)
                  ? parseFloat(this.histogramFiltersData[filterID].min.toFixed(2))
                  : 0,
                max: isFinite(this.histogramFiltersData[filterID].max)
                  ? parseFloat(this.histogramFiltersData[filterID].max.toFixed(2))
                  : this.histogramFiltersData[filterID].data.length
              },
              ...filter
            });
          }
        } else {
          result.push({
            name: this.i18n('subcriteria.' + filterID),
            ...filter
          });
        }
      });

      return result;
    },
    histogramFiltersActive() {
      return this.screening.filters.length;
    },
    selection() {
      return this.$store.state.widget[this.wid].selection;
    },
    country() {
      return this.$store.state.widget[this.wid].selection.settings.country;
    },
    exchange() {
      return this.$store.state.widget[this.wid].selection.settings.exchange;
    },
    sector() {
      return this.$store.state.widget[this.wid].selection.settings.sector;
    },
    screenings() {
      //console.log('getSortedScreenings', this.$store.getters['stockScreenings/getSortedScreenings']);
      return [
        {
          id: 'unsavedSearch',
          name: '  '
        },
        ...this.$store.getters['stockScreenings/getSortedScreenings']
      ];
    },
    screening() {
      return this.selectedScreening
        ? this.$store.getters['stockScreenings/getScreening'](this.selectedScreening)
        : this.$store.state.widget[this.wid].selection.defaultScreening;
    },
    selectedScreening() {
      return this.$store.state.widget[this.wid].selection.selectedScreening; // = ID
    },
    fields() {
      const streamingFields = this.streamingFids.map(f => ({
        key: f,
        label: this.i18n(f + '.short', 'globalLabels.fids'),
        class: 'text-right',
        sortable: f === 'q._TRDPRC_1',
        formatter: (value, key) => {
          if (Number(value) === 0.0) return '0.00';
          return this.$f(value, this.formats[key] || 'float');
        }
      }));

      return [
        {
          key: 'ACTION',
          label: '',
          tdClass: 'text-center',
          thStyle: 'width: 40px'
        },
        {
          key: 'RIC',
          label: this.i18n('labels.ticker'),
          formatter: value => this.$f(value.split('.')[0])
        },
        {
          key: 'COUNTRY',
          label: this.i18n('labels.country')
        }
      ]
        .concat(streamingFields)
        .concat(
          this.histogramFilters
            .filter(h => {
              return !(h.type === 'pattern' && h.status === 'inactive');
            })
            .map(f => ({
              key: f.fid.includes('.') ? f.fid.replace(',', ';').split('.')[1] : f.fid.replace(',', ';'),
              label: this.i18n('subcriteria.' + f.id),
              class: 'text-right',
              sortable: true,
              formatter: value => {
                if (f.type === 'osc') value = value * 100;
                if (f.type === 'pattern') return this.i18n('labels.' + f.status);
                return f.name.indexOf('%') !== -1 ? this.$f(value, 'float.percentage') : this.$f(value, 'float.price');
              }
            }))
        );
    },
    decimalSeparator() {
      return this.$n ? this.$n('0.1')[1] : '.';
    },
    exchangeQueryParam() {
      const isCanada = this.selection.settings.country === 'CA';
      return this.selection.settings.exchange === 'all'
        ? isCanada
          ? this.allCanadaExchanges
          : this.allUSExchanges
        : this.selection.settings.exchange;
    },
    sortBy: {
      get() {
        return this.$store.state.widget[this.wid].tableSort.field;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/SET_SORT_FIELD`, val);
      }
    },
    sortDesc: {
      get() {
        return this.$store.state.widget[this.wid].tableSort.desc;
      },
      set(val) {
        this.$store.commit(`widget/${this.wid}/SET_SORT_DESC`, val);
      }
    },
    defaultQuantity() {
      return this.$store.state.applicationSettings.orders.settings.stocks.defaultOrderQuantity;
    }
  },
  methods: {
    onUpdatePanel(panel, value) {
      this.$store.commit(`widget/${this.wid}/PANEL_CHANGED`, {
        panel,
        value: value !== undefined ? value : !this.panels[panel]
      });
    },
    onUpdateSetting(type, value) {
      this.$store.commit(`widget/${this.wid}/SETTING_CHANGED`, {
        type,
        value
      });

      // Reset exchange filter because the new country will have different exchanges
      if (type === 'country') {
        this.$store.commit(`widget/${this.wid}/SETTING_CHANGED`, {
          type: 'exchange',
          value: 'all'
        });
      }
    },
    onChangeFilterGroup(value) {
      this.$store.commit(`widget/${this.wid}/FILTER_GROUP_CHANGED`, value);
    },
    onAddFilter(filter) {
      //console.log('filter in onAddFilter', filter);
      if (
        !this.unavailableFilters.includes(filter.id) &&
        !this.isFilterActive(filter.id) &&
        this.screening.filters.length < 10
      ) {
        const processedFilter = this.getProcessedFilter(filter);

        if (this.selectedScreening) {
          // PUT processedFilter into database
          this.$store.dispatch(`stockScreenings/addFilter`, {
            screeningID: this.selectedScreening,
            filter: processedFilter
          });
        } else {
          // Add filter to default screening in (widget) state (= currently unsaved search)
          this.$store.commit(`widget/${this.wid}/FILTER_ADDED`, processedFilter);
        }

        this.resultsShown = false;
        this.results = [];

        if (filter.type !== 'pattern') {
          this.fetchHistogramData(filter)
            .then(response => {
              // Check if the fetch response contains data for the asked fid. If not fid is invalid or not in use on server yet
              // NOTE: Maybe to be removed when all endpoints working well -> there shouldnt be an undefined response
              if (response.data.data[filter.id]) {
                // && response.data.data[value.split('.')[1]].count !== 0
                // exclude fids with Infinity as min/max also ?!

                this.$set(this.histogramFiltersData, filter.id, response.data.data[filter.id]);
              } else {
                // Remove filter again from state to not have a broken (undefined) filter in state -> to be considered
                if (this.selectedScreening) {
                  this.onRemoveFilter(filter.id);
                } else {
                  this.$store.commit(`widget/${this.wid}/FILTER_REMOVED`, filter.id);
                }
                this.unavailableFilters.push(filter.id);
                console.warn('Filter currently not available');
              }
            })
            .then(this.fetchCountsData);
        } else {
          this.fetchCountsData();
        }
      } else {
        // Active filter clicked again -> remove filter (if it's not a broken one)
        if (!this.unavailableFilters.includes(filter.id)) {
          this.onRemoveFilter(filter.id);
        }
      }
    },
    getProcessedFilter(filter) {
      return filter.type === 'pattern'
        ? {
            fid: filter.fid,
            id: filter.id,
            type: filter.type,
            group: filter.group,
            status: 'active'
          }
        : {
            fid: filter.fid,
            id: filter.id,
            type: filter.type,
            group: filter.group,
            userExtremes: {
              min: null,
              max: null
            }
          };
    },
    isFilterActive(id) {
      return this.screening.filters.findIndex(c => c.id === id) !== -1;
    },
    isFilterGroupActive(id) {
      return this.screening.filters.findIndex(c => c.group === id) !== -1;
    },
    isFilterDisabled(id) {
      return this.unavailableFilters.includes(id);
    },
    onUpdateHistogramFilter(updateData) {
      if (this.selectedScreening) {
        //console.log('histogram filter in database state changed');
        // update filter data in database by screeningID & updateData/userExtremes
        this.$store.dispatch(`stockScreenings/editFilter`, {
          screeningID: this.selectedScreening,
          filter: {
            id: updateData.id,
            userExtremes: updateData.data
          }
        });
      } else {
        //console.log('histogram filter in widget state changed');
        // Update filter data in default screening in (widget) state (= yet unsaved search)
        this.$store.commit(`widget/${this.wid}/HISTOGRAM_FILTER_CHANGED`, {
          id: updateData.id,
          userExtremes: updateData.data
        });
      }

      this.resultsShown = false;
      this.fetchCountsData();
    },
    onUpdatePatternFilter(updateData) {
      if (this.selectedScreening) {
        //console.log('pattern filter in database state changed');
        // update filter data in database by screeningID & status
        this.$store.dispatch(`stockScreenings/editFilter`, {
          screeningID: this.selectedScreening,
          filter: {
            id: updateData.id,
            status: updateData.data.status
          }
        });
      } else {
        //console.log('pattern filter in widget state changed');
        // Update filter data in default screening in (widget) state (= yet unsaved search)
        this.$store.commit(`widget/${this.wid}/PATTERN_FILTER_CHANGED`, {
          id: updateData.id,
          status: updateData.data.status
        });
      }

      this.resultsShown = false;
      this.fetchCountsData();
    },
    onRemoveFilter(filterID) {
      if (this.selectedScreening) {
        // delete filter from database by screeningID & filterID
        this.$store.dispatch(`stockScreenings/removeFilter`, {
          screeningID: this.selectedScreening,
          filterID
        });
      } else {
        // Delete filter from default screening in (widget) state (= yet unsaved search)
        this.$store.commit(`widget/${this.wid}/FILTER_REMOVED`, filterID);
      }

      this.resultsShown = false;
      this.results = [];

      if (this.histogramFiltersActive) {
        this.fetchCountsData();
      } else {
        this.resultsCount = 0;
      }
    },
    onScreeningSelected(screeningID) {
      this.$store.commit(`widget/${this.wid}/SCREENING_CHANGED`, screeningID);
      this.updateView();
    },
    onScreeningCreated(screening) {
      // Reset filters default screening in state
      this.$store.commit(`widget/${this.wid}/RESET_FILTERS`);

      // Make newly created screening the selected screening
      this.$store.commit(`widget/${this.wid}/SCREENING_CHANGED`, screening);
    },
    onScreeningChanged() {},
    onSortingChanged() {
      this.currentPage = 1;
      this.fetch();
    },
    // Helpers
    getCountryName(countryID) {
      return this.countries.find(c => c.value === countryID).text;
    },
    getExchangeName(exchangeID) {
      return this.exchanges.find(e => e.value === exchangeID).text;
    },
    getSectorName(sectorID) {
      return this.sectors.find(s => s.value === sectorID).text;
    },
    getFidName(fidID) {
      for (let filterGroup in this.filterGroups) {
        for (let fid of this.filterGroups[filterGroup].fids) {
          if (fid.id === fidID) {
            return fid.name;
          }
        }
      }
    },
    getMetaData(fids) {
      return API.Rest.get(`/quote/metadata?fids=${fids}`).then(response => {
        Object.entries(response.data.metadata).forEach(([fid, { type }]) => (this.formats[fid] = type));
      });
    },
    fetch(page = this.currentPage) {
      if (!this.resultsCount) return;

      const requestBody = this.getRequestBody('counts');
      //console.log('requestBody in results', requestBody);
      const fids = ['DSPLY_NAME']
        .concat(this.backgroundFids)
        .concat(
          this.screening.filters.map(c =>
            c.fid.includes('.') ? c.fid.replace(/,/g, ';').split('.')[1] : c.fid.replace(/,/g, ';')
          )
        );

      //Handle sorting
      let sortFid;
      if (this.sortBy && fids.concat(this.streamingFids).includes(this.sortBy)) {
        if (this.sortBy === 'q._TRDPRC_1') {
          sortFid = `${this.sortDesc ? '' : '-'}C(0)`; //special handling for price column
        } else {
          sortFid = `${this.sortDesc ? '' : '-'}${this.sortBy}`;
        }
      } else {
        sortFid = fids.length > 3 ? fids[3] : fids[0];
      }

      const format = /[&<>]/;
      let splitChar = format.test(sortFid) ? sortFid.match(format)[0] : null;
      const sector = this.selection.settings.sector !== 'all' ? `&sectors=${this.selection.settings.sector}` : '';

      this.streamingUnsubscribe();
      this.items = [];
      API.Rest.post(
        `/stockscreener/v2/results?countries=${this.selection.settings.country}&exchanges=${
          this.exchangeQueryParam
        }${sector}&fids=${encodeURIComponent(fids.toString())}&pageNo=${page - 1}&pageSize=${
          this.perPage
        }&sortBy=${encodeURIComponent(sortFid.split(splitChar)[0])}`,
        requestBody
      )
        .then(response => {
          this.results = response.data.data;

          //to initialize table items with all the keys
          const allFields = this.fields.reduce((acc, f) => {
            acc[f.key] = null;
            return acc;
          }, {});
          this.items = this.results.map(item => ({
            ...allFields,
            ...item
          }));
        })
        .then(() => {
          //Initiate streaming
          const rics = this.results.map(r => r.RIC);
          this.streamingConnect()
            .then(this.streamingUnsubscribe)
            .then(() => this.streamingSubscribe(rics));
        });
    },
    fetchCountsData() {
      const requestBody = this.getRequestBody('counts');
      const sector = this.selection.settings.sector !== 'all' ? `&sectors=${this.selection.settings.sector}` : '';

      return API.Rest.post(
        `/stockscreener/v2/counts?countries=${this.selection.settings.country}&exchanges=${this.exchangeQueryParam}${sector}`,
        requestBody
      ).then(res => {
        this.resultsCount = res.data.totalCount;
      });
    },
    fetchHistogramData(filter = null) {
      const requestBody = this.getRequestBody('histogram', filter);
      //console.log('requestBody in fetchHistogramData', requestBody);
      const sector = this.selection.settings.sector !== 'all' ? `&sectors=${this.selection.settings.sector}` : '';

      if (!Object.keys(requestBody).length) {
        return Promise.reject(`Not sending Histograms request. Request Body is empty: ${filter}`);
      }

      return API.Rest.post(
        `/stockscreener/v2/histograms?countries=${this.selection.settings.country}&exchanges=${this.exchangeQueryParam}${sector}`,
        requestBody
      );
    },
    showResults() {
      if (!this.resultsShown) {
        this.resultsShown = true;
      }
      this.fetch(this.currentPage);
      this.onUpdatePanel('results', true);
    },
    //streaming
    streamingConnect() {
      return new Promise((resolve, reject) => {
        if (!API.Streaming.isConnected) {
          API.Streaming.connect({
            timeout: 10000,
            monitor: {
              interval: 10000,
              timeout: 5000
            },
            visibilityMonitorEnabled: true
          }).then(resolve, reject);
        } else {
          resolve();
        }
      });
    },
    streamingUnsubscribe() {
      return new Promise((resolve, reject) => {
        if (this.subscriptionId !== null) {
          API.Streaming.Quote.unsubscribe(this.subscriptionId, (e, err) => {
            if (err) {
              reject(err);
            } else {
              this.subscriptionId = null;
              resolve(e);
            }
          });
        } else {
          resolve();
        }
      });
    },
    streamingSubscribe(rics) {
      this.subscriptionId = API.Streaming.Quote.subscribe(
        {
          rics, // Rics to be streamed
          fids: streamableFidsFilter(this.streamingFids) // Fids to be streamed
        },
        // Subscribe Callback
        this.handleSubscription,
        // Snapshot Callback
        this.handleUpdate,
        // Update Callback
        this.handleUpdate
      );
    },
    handleSubscription(_response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming subscription error', error);
      } else {
        this.hasStreamingError = false;
      }
    },
    // eslint-disable-next-line
    handleUpdate(response, error) {
      if (error) {
        this.hasStreamingError = true;
        console.warn('Streaming update error', error);
        return;
      }

      this.hasStreamingError = false;
      response = response.payload[0];
      const { ric, data } = response;

      try {
        this.items.forEach(row => {
          if (row.RIC === ric) {
            Object.assign(row, data);
          }
        });
      } catch (e) {
        console.error(e);
      }
    },
    // Helpers
    getRequestBody(type, filter) {
      const requestBody = {};

      const data = filter ? [this.screening.filters.find(s => s.id === filter.id)] : this.screening.filters;

      data.forEach(s => {
        const filterID = s.id;
        const filterFid = s.type && (s.type === 'calc' || s.type === 'osc') ? s.fid : s.fid.split('.')[1];

        if (
          s.type === 'fid' ||
          s.type === 'calc' ||
          s.type === 'osc' ||
          (s.type === 'pattern' && s.status && type === 'counts') /* && s.status === 'active' */
        ) {
          requestBody[filterID] = {
            fid: filterFid
          };
        }

        // NOTE: in type === 'histogram' there is no 'pattern', only in 'counts'
        if (type === 'histogram') {
          if (s.type !== 'pattern') {
            requestBody[filterID].resolution = this.histogramResolution;
          }
        } else if (type === 'counts') {
          // If type is pattern AND status is active
          if (s.type === 'pattern') {
            if (requestBody[filterID]) {
              requestBody[filterID].leftBarrier = s.status === 'active' ? 0.9 : 0;
              requestBody[filterID].rightBarrier = s.status === 'active' ? 1 : 0.1;
            }
          } else {
            let leftBarrier = null;
            let rightBarrier = null;

            if (!s.userExtremes.min) s.userExtremes.min = null;
            if (!s.userExtremes.max) s.userExtremes.max = null;

            if (s.userExtremes.min !== null) {
              leftBarrier = s.userExtremes.min;
            } else {
              if (this.histogramFiltersData[filterID] && isFinite(this.histogramFiltersData[filterID].min)) {
                leftBarrier = this.histogramFiltersData[filterID].min;
              } else {
                leftBarrier = 0;
              }
            }
            if (s.userExtremes.max !== null) {
              rightBarrier = s.userExtremes.max;
            } else {
              if (this.histogramFiltersData[filterID] && isFinite(this.histogramFiltersData[filterID].max)) {
                rightBarrier = this.histogramFiltersData[filterID].max;
              } else {
                rightBarrier = this.histogramResolution;
              }
            }

            requestBody[filterID].leftBarrier = leftBarrier;
            requestBody[filterID].rightBarrier = rightBarrier;
          }
        }
      });

      return requestBody;
    },
    clearAll() {
      this.onUpdatePanel('results', false);
      this.$store.commit(`widget/${this.wid}/RESET_FILTERS`); // NOTE: now held in data property temporary as long as search is not saved
      this.$store.commit(`widget/${this.wid}/SCREENING_CHANGED`, '');
      this.resultsCount = 0;
      this.resultsShown = false;
      this.results = [];
    },
    updateView() {
      this.resultsCount = 0;
      this.resultsShown = false;
      this.results = [];

      if (this.screening.filters.length) {
        this.fetchHistogramData()
          .then(response => {
            // NOTE: 13-09-2012 decision for the time being: No resetting of userExtremes - even if they're out of range after a country/exchange/sector change
            // Reset filters in widget state/database state that could be out of range after settings change (country, exchange, sector)
            /*
            if (this.settingsChanged) {
              if (!this.selectedScreening) {
                this.$store.commit(`widget/${this.wid}/RESET_FILTERS_DATA`, response.data.data);
              } else {
                this.$store.dispatch(`stockScreenings/resetFilters`, {
                  screeningID: this.selectedScreening,
                  newFiltersData: response.data.data
                });
              }
            }
            */

            //this.settingsChanged = false;
            this.histogramFiltersData = response.data.data;
          })
          .finally(this.fetchCountsData);
      }
    },
    onRowClicked(item) {
      this.sendLinkUpdate(item.RIC);
    },
    cellClass(field, data) {
      switch (field) {
        case 'q._TRDPRC_1':
        case 'q._NETCHNG_1': {
          if (this.cleanValueForOrders(data.value, this.decimalSeparator) > 0) return 'positive';
          else if (this.cleanValueForOrders(data.value, this.decimalSeparator) < 0) return 'negative';
          else return 'neutral';
        }
        default:
          return '';
      }
    },
    // Helpers
    capitalize(term) {
      return `${term.charAt(0).toUpperCase()}${term.slice(1)}`;
    },
    tabFromCriteria(ev) {
      const el = ev.shiftKey
        ? this.$el.querySelector('div.panel.criteria button')
        : this.$refs.subCriteria.querySelector('.list-group-item');

      if (el) el.focus();
      ev.preventDefault();
    },
    tabFromSubCriteria(ev) {
      const el = ev.shiftKey
        ? this.$refs.criteria.querySelector('.list-group-item:last-child')
        : this.$refs.editCriteria.querySelector('.edit-criteria-content input');

      if (el) el.focus();
      ev.preventDefault();
    },
    navigateUp(ev) {
      const previousElement = ev.target.previousElementSibling;
      if (previousElement) previousElement.focus();
      ev.preventDefault();
    },
    navigateDown(ev) {
      const nextElement = ev.target.nextElementSibling;
      if (nextElement) nextElement.focus();
      ev.preventDefault();
    },
    navigateRight(ev) {
      const el = this.$refs.subCriteria.querySelector('.list-group-item');
      if (el) el.focus();
      ev.preventDefault();
    },
    navigateLeft(ev) {
      const el = this.$refs.criteria.querySelector('.list-group-item:last-child');
      if (el) el.focus();
      ev.preventDefault();
    },
    getExchangesByCountry(country) {
      return stockScreenerBase.filter.exchanges[country].reduce((result, v) => {
        if (v.value !== 'all') {
          result.push(v.value);
        }
        return result;
      }, []);
    }
  },
  created() {
    this.getMetaData(this.streamingFids);
    this.allCanadaExchanges = this.getExchangesByCountry('CA');
    this.allUSExchanges = this.getExchangesByCountry('US');
  },
  mounted() {
    if (this.histogramFiltersActive) {
      this.fetchHistogramData()
        .then(response => {
          this.histogramFiltersData = response.data.data;
        })
        .finally(this.fetchCountsData);
    }
  },
  beforeDestroy() {
    this.streamingUnsubscribe();
  }
};
</script>
<style lang="scss">
@import '~@/styles/widgets/stock-screener-widget';
</style>
